import { sanitizeCssValue as sc } from "@app/widgets/shared/btnHoverCss";

export type AccordionWidgetProps = {
  allowMultiple: boolean;
  defaultOpenFirst: boolean;
  expandIcon: string;
  headerBgColor: string;
  headerTextColor: string;
  headerFontFamily: string;
  headerPaddingV: string;
  headerPaddingH: string;
  headerBorderColor: string;
  bodyBgColor: string;
  bodyPaddingV: string;
  bodyPaddingH: string;
  borderRadius: string;
  animationSpeed: string;
};

export const parseAccordionProps = (container: HTMLElement): AccordionWidgetProps => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;
  return {
    allowMultiple: attr("data-allow-multiple", "true") === "true",
    defaultOpenFirst: attr("data-default-open-first", "true") === "true",
    expandIcon: attr("data-expand-icon", "arrow"),
    headerBgColor: attr("data-header-bg-color", "#fafafa"),
    headerTextColor: attr("data-header-text-color", "#333333"),
    headerFontFamily: attr("data-header-font-family"),
    headerPaddingV: attr("data-header-padding-v", "12px"),
    headerPaddingH: attr("data-header-padding-h", "16px"),
    headerBorderColor: attr("data-header-border-color", "#d9d9d9"),
    bodyBgColor: attr("data-body-bg-color", "#ffffff"),
    bodyPaddingV: attr("data-body-padding-v", "16px"),
    bodyPaddingH: attr("data-body-padding-h", "16px"),
    borderRadius: attr("data-border-radius", "8px"),
    animationSpeed: attr("data-animation-speed", "0.3s"),
  };
};

type PanelData = {
  key: string;
  title: string;
  content: HTMLElement;
  isOpen: boolean;
};

export const initAccordion = (container: HTMLElement, props: AccordionWidgetProps) => {
  if (container.dataset.ezInit === "accordion") return;

  const itemEls = Array.from(container.querySelectorAll<HTMLElement>(
    ":scope > .ez-accordion-item, :scope > div > .ez-accordion-item"
  ));
  if (itemEls.length === 0) return;

  container.dataset.ezInit = "accordion";

  const panels: PanelData[] = itemEls.map((el, i) => {
    const headerEl = el.querySelector(".ez-accordion-item-header");
    const headerTitle = headerEl?.textContent?.trim();
    headerEl?.remove();

    const bodyWrapper = el.querySelector(".ez-accordion-item-body");
    if (bodyWrapper) {
      const fragment = document.createDocumentFragment();
      while (bodyWrapper.firstChild) fragment.appendChild(bodyWrapper.firstChild);
      el.innerHTML = "";
      el.appendChild(fragment);
    } else {
      const firstChild = el.firstElementChild;
      if (firstChild && !firstChild.classList.contains("ez")) {
        const grandChildren = Array.from(firstChild.children);
        if (grandChildren.length >= 2 && (grandChildren[0] as HTMLElement).style?.padding) {
          const body = grandChildren[1] as HTMLElement;
          const fragment = document.createDocumentFragment();
          while (body.firstChild) fragment.appendChild(body.firstChild);
          el.innerHTML = "";
          el.appendChild(fragment);
        }
      }
    }

    return {
      key: String(i),
      title: headerTitle || el.getAttribute("data-accordion-title") || `Panel ${i + 1}`,
      content: el,
      isOpen: el.getAttribute("data-accordion-open") === "true",
    };
  });

  if (props.defaultOpenFirst && panels.length > 0 && !panels.some(p => p.isOpen)) {
    panels[0].isOpen = true;
  }

  container.innerHTML = "";
  container.style.overflow = "hidden";
  container.style.borderRadius = sc(props.borderRadius || "8px");
  container.style.border = `1px solid ${sc(props.headerBorderColor || "#d9d9d9")}`;

  const style = document.createElement("style");
  const speed = sc(props.animationSpeed || "0.3s");
  const headerBg = sc(props.headerBgColor || "#fafafa");
  const headerColor = sc(props.headerTextColor || "#333");
  const headerFontFamily = props.headerFontFamily ? sc(props.headerFontFamily) : "";
  const headerPadV = sc(props.headerPaddingV || "12px");
  const headerPadH = sc(props.headerPaddingH || "16px");
  const headerBorder = sc(props.headerBorderColor || "#d9d9d9");
  const bodyBg = sc(props.bodyBgColor || "#fff");
  const bodyPadV = sc(props.bodyPaddingV || "16px");
  const bodyPadH = sc(props.bodyPaddingH || "16px");
  style.textContent = `
    .ez-acc-header {
      background: ${headerBg};
      color: ${headerColor};
      ${headerFontFamily ? `font-family: ${headerFontFamily};` : ""}
      padding: ${headerPadV} ${headerPadH};
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
      gap: 10px;
      border-bottom: 1px solid ${headerBorder};
      font-weight: 500;
      transition: background ${speed};
    }
    .ez-acc-header:hover {
      filter: brightness(0.97);
    }
    .ez-acc-header .ez-acc-icon {
      transition: transform ${speed};
      flex-shrink: 0;
      font-size: 12px;
    }
    .ez-acc-header[data-open="true"] .ez-acc-icon-arrow {
      transform: rotate(90deg);
    }
    .ez-acc-header[data-open="true"] .ez-acc-icon-plus {
      transform: rotate(45deg);
    }
    .ez-acc-body {
      background: ${bodyBg};
      overflow: hidden;
      transition: max-height ${speed} ease, padding ${speed} ease;
      max-height: 0;
      padding: 0 ${bodyPadH};
    }
    .ez-acc-body[data-open="true"] {
      max-height: 5000px;
      padding: ${bodyPadV} ${bodyPadH};
    }
    .ez-acc-panel:last-child .ez-acc-header {
      border-bottom: ${panels.length > 1 ? `1px solid ${headerBorder}` : "none"};
    }
    .ez-acc-panel:last-child .ez-acc-body[data-open="true"] + .ez-acc-header,
    .ez-acc-panel:last-child .ez-acc-header:not([data-open="true"]) {
      border-bottom: none;
    }
  `;
  container.appendChild(style);

  const makeIcon = (): HTMLElement | null => {
    if (props.expandIcon === "none") return null;
    const span = document.createElement("span");
    span.className = props.expandIcon === "plus" ? "ez-acc-icon ez-acc-icon-plus" : "ez-acc-icon ez-acc-icon-arrow";
    span.textContent = props.expandIcon === "plus" ? "+" : "▸";
    return span;
  };

  panels.forEach((panel) => {
    const panelDiv = document.createElement("div");
    panelDiv.className = "ez-acc-panel";

    const header = document.createElement("div");
    header.className = "ez-acc-header";
    header.setAttribute("data-open", panel.isOpen ? "true" : "false");
    const icon = makeIcon();
    if (icon) header.appendChild(icon);
    const titleSpan = document.createElement("span");
    titleSpan.textContent = panel.title;
    header.appendChild(titleSpan);

    const body = document.createElement("div");
    body.className = "ez-acc-body";
    body.setAttribute("data-open", panel.isOpen ? "true" : "false");
    body.appendChild(panel.content);

    header.addEventListener("click", () => {
      const isOpen = header.getAttribute("data-open") === "true";
      if (!props.allowMultiple) {
        container.querySelectorAll(".ez-acc-header").forEach(h => h.setAttribute("data-open", "false"));
        container.querySelectorAll<HTMLElement>(".ez-acc-body").forEach(b => { b.setAttribute("data-open", "false"); b.style.maxHeight = "0px"; });
      }
      const open = !isOpen;
      header.setAttribute("data-open", open ? "true" : "false");
      body.setAttribute("data-open", open ? "true" : "false");
      body.style.maxHeight = open ? `${body.scrollHeight}px` : "0px";
    });

    panelDiv.appendChild(header);
    panelDiv.appendChild(body);
    container.appendChild(panelDiv);
  });

  requestAnimationFrame(() => {
    container.querySelectorAll<HTMLElement>('.ez-acc-body[data-open="true"]').forEach((b) => {
      b.style.maxHeight = `${b.scrollHeight}px`;
    });
  });
};
